import NotificationsIcon from '@mui/icons-material/Notifications'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff'
import { FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { RootState } from 'dataStore/state'
import {
  areAdminNewOrderNotificationsEnabled,
  setAdminNewOrderNotificationsEnabled,
} from 'handlers/adminNewOrderNotificationsPreference'
import { getNotificationsSupportStatus } from 'handlers/getNotificationsSupportStatus'
import { openPwaInstallGuide } from 'handlers/openPwaInstallGuide'
import { requestNotificationPermission } from 'handlers/showNewOrderNotification'
import { subscribeAdminPush } from 'handlers/subscribeAdminPush'
import { unsubscribeAdminPush } from 'handlers/unsubscribeAdminPush'

import { NotificationsIconButton } from './EnableNotificationsButton.styles'

/**
 * Кнопка в хедере: вкл/выкл Browser Notification + Web Push по одному клику.
 */
const EnableNotificationsButton: FC = () => {
  const filterPizzeriaId = useSelector(
    (state: RootState) => state.orders.filter?.pizzeria_id ?? null
  )
  const [permission, setPermission] = useState<NotificationPermission>(() => {
    if (typeof Notification === 'undefined') {
      return 'denied'
    }
    return Notification.permission
  })
  const [isEnabled, setIsEnabled] = useState<boolean>(() =>
    areAdminNewOrderNotificationsEnabled()
  )

  useEffect(() => {
    if (typeof Notification === 'undefined') {
      return
    }
    setPermission(Notification.permission)
    setIsEnabled(areAdminNewOrderNotificationsEnabled())
  }, [])

  /**
   * Выключает уведомления: отписка от push (в т.ч. фон PWA) + локальный флаг + тост.
   */
  const handleDisableNotifications = async (): Promise<void> => {
    try {
      await unsubscribeAdminPush()
      setAdminNewOrderNotificationsEnabled(false)
      setIsEnabled(false)
      toast.info('Уведомления выключены')
    } catch (error: unknown) {
      console.error(error)
      // Флаг всё равно снимаем — не шумим в открытой вкладке; push попробуем снять ещё раз
      setAdminNewOrderNotificationsEnabled(false)
      setIsEnabled(false)
      toast.warning(
        error instanceof Error
          ? error.message
          : 'Уведомления в приложении выключены, но фоновую подписку снять не удалось. Попробуйте ещё раз.'
      )
    }
  }

  /**
   * Включает уведомления: permission ОС → подписка Web Push.
   */
  const handleEnableNotifications = async (): Promise<void> => {
    const supportStatus = getNotificationsSupportStatus()

    if (supportStatus === 'needs_ios_pwa_install') {
      toast.info(
        'На iPhone/iPad уведомления работают только из установленного приложения: Поделиться → На экран «Домой», затем откройте ярлык и нажмите снова.'
      )
      openPwaInstallGuide()
      return
    }

    if (supportStatus === 'unsupported_notification_api') {
      toast.error(
        'Уведомления не поддерживаются в этом браузере. Откройте админку в Safari (iOS) или Chrome/Edge (десктоп) по HTTPS.'
      )
      return
    }

    // Первый await сразу после sync-проверок — сохраняем user gesture для диалога ОС
    const nextPermission = await requestNotificationPermission()
    setPermission(nextPermission)

    if (nextPermission === 'granted') {
      if (supportStatus === 'push_unsupported') {
        setAdminNewOrderNotificationsEnabled(true)
        setIsEnabled(true)
        toast.warning(
          'Разрешение получено. Фоновый Web Push в этом браузере недоступен — уведомления будут при открытой вкладке.'
        )
        return
      }

      try {
        await subscribeAdminPush(
          filterPizzeriaId == null ? null : Number(filterPizzeriaId)
        )
        setAdminNewOrderNotificationsEnabled(true)
        setIsEnabled(true)
        toast.success(
          'Уведомления включены: новые заказы и результат онлайн-оплаты (включая фон)'
        )
      } catch (error: any) {
        console.error(error)
        // Permission есть — включаем локальные уведомления даже без push
        setAdminNewOrderNotificationsEnabled(true)
        setIsEnabled(true)
        toast.warning(
          error?.message ||
            'Разрешение получено, но фоновый Web Push не удалось включить'
        )
      }
      return
    }

    if (nextPermission === 'denied') {
      setAdminNewOrderNotificationsEnabled(false)
      setIsEnabled(false)
      toast.error(
        'Уведомления запрещены. Разрешите их в настройках браузера или в настройках установленного приложения.'
      )
      return
    }

    toast.info('Разрешение на уведомления не получено')
  }

  /**
   * Переключает уведомления: при включённых — выключает, иначе включает.
   */
  const handleToggleNotifications = async (): Promise<void> => {
    if (permission === 'granted' && isEnabled) {
      await handleDisableNotifications()
      return
    }
    await handleEnableNotifications()
  }

  const NotificationStateIcon =
    permission === 'granted' && isEnabled
      ? NotificationsActiveIcon
      : permission === 'denied'
        ? NotificationsOffIcon
        : NotificationsIcon

  const ariaLabel =
    permission === 'granted' && isEnabled
      ? 'Выключить уведомления о новых заказах'
      : 'Включить уведомления о новых заказах'

  return (
    <NotificationsIconButton
      color='inherit'
      aria-label={ariaLabel}
      title={ariaLabel}
      onClick={() => {
        void handleToggleNotifications()
      }}
    >
      <NotificationStateIcon />
    </NotificationsIconButton>
  )
}

export default EnableNotificationsButton
